import type { ApiError as ApiErrorBody } from "@tasduvar/shared";

/** API kökü: üretimde aynı origin (`/api`), geliştirmede Vite proxy. `VITE_API_BASE` ile değiştirilebilir. */
export const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

/** Sunucunun döndürdüğü hata (HTTP ≥ 400, JSON gövdeli). */
export class ApiClientError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly body: unknown = null) {
    super(message);
    this.name = "ApiClientError";
  }
}
/** Ağ yok / sunucu yok / JSON değil (ör. API'siz statik yayın) — misafir moduna düşülür. */
export class NetworkError extends Error {
  constructor(message = "Sunucuya ulaşılamıyor") { super(message); this.name = "NetworkError"; }
}

const listeners = new Set<() => void>();
/** 401 alındığında oturum düştü sayılır; useAuth burada dinler. */
export function onUnauthorized(fn: () => void): () => void { listeners.add(fn); return () => listeners.delete(fn); }

export async function api<T = unknown>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method, credentials: "include",
      headers: body === undefined ? {} : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch { throw new NetworkError(); }
  const isJson = (res.headers.get("content-type") ?? "").includes("application/json");
  if (!isJson) {
    if (res.ok && res.status === 204) return undefined as T;
    if (res.headers.get("content-type")?.includes("text/csv")) return (await res.text()) as T;
    throw new NetworkError(`API yanıtı beklenmedik biçimde (HTTP ${res.status})`);
  }
  const data: unknown = await res.json();
  if (!res.ok) {
    const e = (data ?? {}) as Partial<ApiErrorBody>;
    if (res.status === 401) listeners.forEach((fn) => fn());
    throw new ApiClientError(res.status, e.code ?? "HTTP_" + res.status, e.error ?? `İstek başarısız (HTTP ${res.status})`, data);
  }
  return data as T;
}

export const isApiError = (e: unknown, code?: string): e is ApiClientError => e instanceof ApiClientError && (code === undefined || e.code === code);
export const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : "Beklenmeyen hata");
